// pages/manager/ManagerComplaintsPage.jsx
//
// Manager -> Team Complaints (/manager/complaints). The SAME table as
// Manager Home's "Ticket Assignment & Progress" (ManagerTaskTable.jsx), over
// the manager's own-unit IT Support complaints only.
//
// Data scope is server-side:
//   GET /tasks -- the caller's tasks:view scope (UNIT for a Manager, from
//     req.user.unit_id). Every row carries its server-derived ticket_flow and
//     work_group (services/ticketFlowService.js), which drive the tabs.
//   GET /manager/team-users -- own-unit roster; the only source of Assign
//     Tech options. PUT /tasks/assign/:id re-checks both units server-side.
//   Category / Subcategory are shown from each complaint's own saved values
//     (category_name / sub_category_name); there is no category edit here
//     (PUT /tasks/:id/category never existed). Formerly: GET
//     /categories/by-unit/:unit_id (falls back to GET /categories, as Home does, only when the
//     session carries no unit).
// Actions match Home: Open (ticket chat / remarks), Save (assignment +
// category). /manager/complaints?status=<status> pre-selects the status
// filter (Home's KPI cards link here that way).
//
// Work-flow tabs (All / Developer / Data Analyst / Technician / IT Support)
// follow the ticket's WORK GROUP -- derived on the server from its flow
// (IT Support complaint / Own Ticket / task type) and current owner, never
// from the creator's role alone (utils/workGroup.js):
//   IT_SUPPORT_COMPLAINT                      -> Technician / IT Support
//   Developer Own Ticket, DEVELOPMENT / BUG /
//     TESTING / DEPLOYMENT                    -> Developer
//   Data Analyst Own Ticket                   -> Data Analyst
//   other types                               -> the current owner's role
// So a Developer-created IT Support complaint is in Technician / IT Support,
// not Developer. Each row's assignee options follow the same group
// (ManagerTaskTable#buildAssignOptions). Created By, Current Owner, flow and
// saved Category/Subcategory stay separate columns. Filtering is
// client-side: GET /tasks returns the whole own-unit set (unpaginated) and
// every tab needs a count anyway.
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FaCircleCheck, FaCircleXmark } from "react-icons/fa6";
import api from "../../api";
import { AuthContext } from "../../context/AuthContext";
import TaskChatPanel from "../tasks/TaskChatPanel";
import ManagerTaskTable, {
  Spin, getText, normalizeStatus,
  buildStatusRows, buildInitialDrafts, buildTeamFilterMembers, filterTaskRows,
} from "./ManagerTaskTable";
import { WORK_GROUP_TABS, filterByWorkGroup, workGroupOf } from "../../utils/workGroup";
import "./ManagerDashboard.css";
import "../technicians/TechnicianDesignSystem.css";

// Assign column label per tab (the row's own group decides its options).
const ASSIGN_LABEL = {
  all: "Assign To",
  developer: "Assign Developer",
  "data-analyst": "Assign Data Analyst",
  technician: "Assign Technician",
};

export default function ManagerComplaintsPage() {
  const { user } = useContext(AuthContext) || {};
  const [searchParams] = useSearchParams();

  const [tasks, setTasks] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [chatTaskId, setChatTaskId] = useState(null);
  const [toast, setToast] = useState({ show: false, msg: "", variant: "success" });

  const [search, setSearch] = useState("");
  const [statusF, setStatusF] = useState(() => {
    const requested = searchParams.get("status");
    return requested ? normalizeStatus(requested) : "ALL";
  });
  const [member, setMember] = useState("ALL");
  const [quickFilter, setQuickFilter] = useState("all");
  const [roleTab, setRoleTab] = useState("all");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const pop = (msg, variant = "success") => {
    setToast({ show: true, msg, variant });
    setTimeout(() => setToast((t) => ({ ...t, show: false })), 3000);
  };

  const unitId = user?.unit_id;
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [taskRes, userRes] = await Promise.allSettled([
        api.get("/tasks"),
        api.get("/manager/team-users"),
      ]);
      const taskData = taskRes.status === "fulfilled" && Array.isArray(taskRes.value.data) ? taskRes.value.data : [];
      const userData = userRes.status === "fulfilled" && Array.isArray(userRes.value.data) ? userRes.value.data : [];
      if (taskRes.status === "rejected") pop("Failed to load team tickets", "danger");

      // Only the three work flows this page manages: a ticket with no work
      // group (e.g. an unassigned ERP/CRM task, GENERAL work owned by Staff)
      // is not shown, so "All" = Developer + Data Analyst + Technician / IT
      // Support. The server already scopes by unit; the unit check only
      // guards the render (a row from another unit is never shown).
      const cleaned = taskData
        .filter((t) => workGroupOf(t))
        .filter((t) => !unitId || t.unit_id === null || t.unit_id === undefined || Number(t.unit_id) === Number(unitId))
        .map((t) => ({ ...t, status: normalizeStatus(t.status) }));
      setTasks(cleaned);
      setTeamMembers(userData);
      setDrafts(buildInitialDrafts(cleaned));
    } finally {
      setLoading(false);
    }
  }, [unitId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const now = useMemo(() => new Date(), [tasks]); // eslint-disable-line react-hooks/exhaustive-deps
  // The table (chips, status options, pagination) sees only the selected
  // work group's tickets.
  const roleRows = useMemo(() => filterByWorkGroup(tasks, roleTab), [tasks, roleTab]);
  const statusRows = useMemo(() => buildStatusRows(roleRows), [roleRows]);
  // Tab counts honour the page's other filters (search / status / user /
  // chip), so each number matches the rows that tab would show.
  const roleCounts = useMemo(() => {
    const visible = filterTaskRows(tasks, { search, status: statusF, member, quickFilter }, now);
    return Object.fromEntries(WORK_GROUP_TABS.map(([key]) => [key, filterByWorkGroup(visible, key).length]));
  }, [tasks, search, statusF, member, quickFilter, now]);
  const changeRoleTab = (key) => { setRoleTab(key); setPage(1); };
  const memberOptions = useMemo(() => buildTeamFilterMembers(teamMembers, user?.id), [teamMembers, user?.id]);

  const updateDraft = (id, field, value) => setDrafts((p) => ({ ...p, [id]: { ...(p[id] || {}), [field]: value } }));

  // Same calls as Home's saveAssignment().
  const saveAssignment = async (task) => {
    const draft = drafts[task.id] || {};
    if (!draft.assigned_to) { pop("Please select an assignee", "danger"); return; }
    try {
      setSavingId(task.id);
      await api.put(`/tasks/assign/${task.id}`, { assigned_to: Number(draft.assigned_to) });
      await fetchData();
      pop("Ticket assigned successfully");
    } catch (e) {
      pop(e?.response?.data?.error || "Failed to assign ticket", "danger");
    } finally {
      setSavingId(null);
    }
  };

  const resetFilters = () => { setRoleTab("all"); setSearch(""); setStatusF("ALL"); setMember("ALL"); setQuickFilter("all"); setPage(1); };

  return (
    // .tech-ds + tech-page-* = the Ticket Dashboard's page frame and header
    // (technicians/TechnicianDesignSystem.css); the table styles itself
    // (ManagerTaskTable.css) from the same --tech-* tokens.
    <div className="md-page tech-ds">
      <div className="tech-page-header">
        <div>
          <div className="tech-section-label">Complaint Management</div>
          <h1 className="tech-page-title">Team Complaints</h1>
          <p className="tech-page-subtitle">View, assign and track your team's tickets by work flow — IT Support complaints, Developer and Data Analyst work.</p>
        </div>
        <div className="tech-header-actions">
          <button type="button" className="unit-btn unit-btn-light" onClick={fetchData} disabled={loading}>
            {loading ? <Spin /> : <i className="fa-solid fa-arrow-rotate-right" aria-hidden="true"></i>}
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      <div className="md-tabs mc-role-tabs" role="tablist" aria-label="Tickets by work flow">
        {WORK_GROUP_TABS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={roleTab === key}
            className={`md-tab${roleTab === key ? " active" : ""}`}
            onClick={() => changeRoleTab(key)}
          >
            {label} <span className="mc-role-tab-count">({roleCounts[key]})</span>
          </button>
        ))}
      </div>

      <ManagerTaskTable
        standardized
        showCreatedBy
        assignLabel={ASSIGN_LABEL[roleTab] || "Assign To"}
        title="Team Complaints"
        subtitle={`Tickets in your unit${getText(user?.unit_name, "") ? ` (${user.unit_name})` : ""} — each row is assigned within its own work flow: Technicians for IT Support complaints, Developers / Data Analysts for their work.`}
        rows={roleRows}
        loading={loading}
        now={now}
        search={search} onSearchChange={setSearch}
        status={statusF} onStatusChange={setStatusF} statusOptions={statusRows}
        member={member} onMemberChange={setMember} memberOptions={memberOptions}
        quickFilter={quickFilter} onQuickFilterChange={setQuickFilter}
        page={page} onPageChange={setPage}
        rowsPerPage={rowsPerPage} onRowsPerPageChange={setRowsPerPage}
        onReset={resetFilters}
        extraFiltersActive={roleTab !== "all"}
        drafts={drafts} onDraftChange={updateDraft}
        teamMembers={teamMembers}
        onOpen={(task) => setChatTaskId(task.id)}
        onSave={saveAssignment}
        savingId={savingId}
      />

      <TaskChatPanel
        taskId={chatTaskId}
        show={chatTaskId !== null}
        onClose={() => setChatTaskId(null)}
        currentUserId={user?.id}
        onSent={fetchData}
      />

      {toast.show && (
        <div className={`md-toast ${toast.variant}`}>
          {toast.variant === "success" ? <FaCircleCheck /> : <FaCircleXmark />} {toast.msg}
        </div>
      )}
    </div>
  );
}
